import { T } from '@transifex/react';
import { Typography } from 'src/ui/Typography';
import { Col, Row, Tabs } from 'src/antd';
import { useGetToken } from 'src/api/config';
import { TAllowedArcValue } from 'src/api/consts/CriptioArcValues';
import { TCompanyStore, useCompany } from 'src/app/Company.store';
import { getAuthPropertyId } from 'src/api/state';
import React, {
  CSSProperties,
  ComponentProps,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import styled from 'styled-components';
import useMediaQuery from 'use-media-antd-query/es/useMediaQuery';

import { buildEidRedirectUrl as createMitIdLink } from 'src/api/eidRedirect';
import {
  isFinnishTrustNetworkProvider,
  isIdPortenProvider,
} from 'src/api/eidProviders';
import MitIDIcon from './MitID_logo.svg';
import NemIDIcon from './NemId_logo.svg';
import BankIDIcon from './BankID-DNA-element-white.svg';

const { TabPane } = Tabs;

const MitLoginBtn = styled.button`
  background: #0060e6;
  color: ${(props) => props.theme.colors.white100};
  font-family: 'IBM Plex Sans', inherit;
  border-radius: 4px;
  border: none;
  height: 48px;
  display: flex;
  font-weight: 500;
  justify-content: center;
  align-items: center;
  padding: 0.25rem 0.75rem 0.25rem 1rem;
  cursor: pointer;
  transition: background-color 150ms ease-out;
  &:hover {
    background-color: #004cb8;
  }
`;

const NemLoginBtn = styled.button`
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  background: ${(props) => props.theme.colors.white100};
  padding: 8px;
  cursor: pointer;
  .icon {
    margin-right: 8px;
  }
`;

const BankIDLoginBtn = styled.button`
  background: #39134c;
  color: #ffffff;
  font-family: 'Roboto', 'IBM Plex Sans', inherit;
  font-weight: 500;
  font-size: 18px;
  border-radius: 25px;
  border: none;
  min-height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  gap: 14px;
  cursor: pointer;
  transition: background-color 150ms ease-out;
  &:hover {
    background-color: #470d70;
  }
`;

// Suomi.fi / FTN button. Placeholder styling in Suomi.fi navy — replace with the
// official Suomi.fi identification button per FTN brand guidelines when the asset
// lands (the broker's own picker handles bank/Mobiilivarmenne selection).
const SuomiFiLoginBtn = styled.button`
  background: #003479;
  color: #ffffff;
  font-family: 'IBM Plex Sans', inherit;
  font-weight: 500;
  font-size: 16px;
  border-radius: 4px;
  border: none;
  min-height: 48px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 24px;
  cursor: pointer;
  transition: background-color 150ms ease-out;
  &:hover {
    background-color: #002a63;
  }
`;

const StyledTab = styled(Tabs)<{ hideTabs: boolean }>`
  width: 100%;
  .ant-tabs-nav {
    display: ${({ hideTabs }) => (hideTabs ? 'none' : 'flex')};
  }
`;

const getLabel = (arcValue: TAllowedArcValue, business: boolean) => {
  if (arcValue === 'urn:grn:authn:dk:nemid:poces') {
    return business ? (
      <T _str="Business (Company signatories) with code card" />
    ) : (
      <T _str="Personal with code card" />
    );
  }
  if (arcValue === 'urn:grn:authn:dk:nemid:moces') {
    return business ? <T _str="Business (Employee) with code card" /> : null;
  }
  if (arcValue === 'urn:grn:authn:dk:nemid:moces:codefile') {
    return business ? <T _str="Business (Employee) with code file" /> : null;
  }
  return null;
};

const CriptioLoginUI = ({
  provider,
  url,
  onClick,
  acr_values,
  business,
}: TCompanyStore['data']['authentication_urls'][0] & {
  onClick?: () => void;
}) => {
  const token = useGetToken();

  if (provider === 'mitid') {
    return (
      <MitLoginBtn
        onClick={() => {
          const propertyId = getAuthPropertyId();
          onClick?.();
          const href = createMitIdLink(url, token, propertyId);
          window.location.href = href;
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src={MitIDIcon} style={{ width: '50px', marginRight: '1rem' }} />
        <span>Log ind med MitID</span>
      </MitLoginBtn>
    );
  }

  if (provider === 'bankid') {
    return (
      <BankIDLoginBtn
        onClick={() => {
          onClick?.();
          const href = createMitIdLink(url, token);
          window.location.href = href;
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img src={BankIDIcon} style={{ height: '28px', width: 'auto' }} />
        <T _str="Log in with BankID" />
      </BankIDLoginBtn>
    );
  }

  if (isFinnishTrustNetworkProvider(provider)) {
    return (
      <SuomiFiLoginBtn
        onClick={() => {
          const propertyId = getAuthPropertyId();
          onClick?.();
          const href = createMitIdLink(url, token, propertyId);
          window.location.href = href;
        }}
      >
        <T _str="Identify with Suomi.fi" />
      </SuomiFiLoginBtn>
    );
  }

  // else render the nemid login buttons
  return (
    <NemLoginBtn
      onClick={() => {
        const propertyId = getAuthPropertyId();
        const href = createMitIdLink(url, token, propertyId);
        window.location.href = href;
      }}
      style={{ fontWeight: 400, fontSize: 16, lineHeight: 2 }}
    >
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img src={NemIDIcon} style={{ width: '50px', marginRight: '8px' }} />
      <span>{getLabel(acr_values, business)}</span>
    </NemLoginBtn>
  );
};

// label map for provider and use T tag
const providerLabelMap = {
  mitid: <T _str="MitID" />,
  nemid: <T _str="NemID" />,
  bankid: <T _str="BankID" />,
  suomifi: <T _str="Suomi.fi" />,
};

// label map for business and personal use T tag
const businessLabelMap = {
  business: <T _str="Business" />,
  personal: <T _str="Personal" />,
};

const CriptioLogin = ({
  style,
  onClick,
}: { style?: CSSProperties } & Pick<
  ComponentProps<typeof CriptioLoginUI>,
  'onClick'
>) => {
  const { data: company } = useCompany();
  const isMobile = useMediaQuery('(max-width: 996px)');

  // from the company authentication urls, create data for following shape.
  /*
 {
    mitid: {
      business: Array<AuthenticationUrl>,
      personal: Array<AuthenticationUrl>,
    },
    nemid: {
      business: Array<AuthenticationUrl>,
      personal: Array<AuthenticationUrl>,
    },
    bankid: {
      business: Array<AuthenticationUrl>,
      personal: Array<AuthenticationUrl>,
    },
 }
*/

  // Tab keys, not raw backend provider values — Suomi.fi arrives as either
  // 'suomifi' (what the backend emits) or the legacy 'ftn' spelling, and both
  // collapse into the one 'suomifi' tab.
  type TProvider = 'mitid' | 'nemid' | 'bankid' | 'suomifi';

  const authenticationUrls = company.authentication_urls.reduce(
    (acc, v) => {
      const { provider, business } = v;
      if (provider === 'mitid') {
        acc.mitid[business ? 'business' : 'personal'].push(v);
      } else if (provider === 'bankid') {
        acc.bankid[business ? 'business' : 'personal'].push(v);
      } else if (isFinnishTrustNetworkProvider(provider)) {
        acc.suomifi[business ? 'business' : 'personal'].push(v);
      } else if (isIdPortenProvider(provider)) {
        // Dropped on purpose — no bucket, no tab. ID-porten has its own surface
        // (IdPortenLogin) and must never reach the NemID catch-all below:
        // Digdir's terms forbid another eID's name or logo on an ID-porten
        // service, so a Norwegian citizen would be shown a Danish NemID button.
        // A tenant configured for ID-porten carries `login_method: 'idporten'`
        // with it and never renders this component, so this branch only fires
        // when ID-porten is bolted onto a Criipto tenant — where omitting the
        // entry is the safe failure and the wrong brand is not.
      } else {
        // Everything else is NemID — note this is a catch-all, so any provider
        // the buckets above miss renders NemID branding. That is exactly how a
        // 'suomifi' entry used to be mislabelled.
        acc.nemid[business ? 'business' : 'personal'].push(v);
      }
      return acc;
    },
    {
      mitid: {
        business: [],
        personal: [],
      },
      nemid: {
        business: [],
        personal: [],
      },
      bankid: {
        business: [],
        personal: [],
      },
      suomifi: {
        business: [],
        personal: [],
      },
    } as Record<
      TProvider,
      {
        business: TCompanyStore['data']['authentication_urls'];
        personal: TCompanyStore['data']['authentication_urls'];
      }
    >
  );

  const tabKeys = Object.keys(authenticationUrls).filter((provider) => {
    const hasUrls =
      authenticationUrls[provider as TProvider].business.length > 0 ||
      authenticationUrls[provider as TProvider].personal.length > 0;
    if (!hasUrls) return false;
    // `login_method: 'bankid'` was written when BankID was the only non-Danish
    // provider, so it read "BankID only". Suomi.fi has to be admitted alongside
    // it, otherwise a tenant that offers both renders BankID and silently drops
    // Suomi.fi — the live `hra-portal` tenant is exactly this shape (Norwegian,
    // `login_method: 'bankid'` on production, and a `suomifi` provider on
    // preview-2). A provider value says nothing about the tenant's country, so
    // Nordic providers must be able to coexist.
    //
    // This cannot surface Suomi.fi on a tenant that did not ask for it: the
    // entry only exists here when the backend put a `suomifi` URL in
    // `authentication_urls`, which is the tenant's opt-in.
    if (company.login_method === 'bankid') {
      return provider === 'bankid' || provider === 'suomifi';
    }
    if (company.login_method === 'user') return provider !== 'bankid';
    return true;
  });

  return (
    <StyledTab centered style={style} hideTabs={tabKeys.length === 1}>
      {tabKeys.map((provider) => {
        const { business, personal } =
          authenticationUrls[provider as TProvider];

        if (provider === 'bankid' || provider === 'suomifi') {
          const firstUrl = [...personal, ...business][0];
          if (!firstUrl) return null;
          return (
            <TabPane
              tab={providerLabelMap[provider as TProvider]}
              key={provider}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <CriptioLoginUI {...firstUrl} onClick={onClick} />
              </div>
            </TabPane>
          );
        }

        return (
          <TabPane tab={providerLabelMap[provider as TProvider]} key={provider}>
            <Row gutter={[16, 16]}>
              {React.Children.toArray(
                [personal, business].map((urls, i) => {
                  return (
                    <Col span={isMobile ? 24 : 12}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          rowGap: 8,
                          flexDirection: 'column',
                        }}
                      >
                        <Typography weight="semi-bold" variant="h6">
                          {businessLabelMap[i === 0 ? 'personal' : 'business']}
                        </Typography>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            rowGap: 8,
                            flexDirection: 'column',
                          }}
                        >
                          {urls.map((url) => (
                            <CriptioLoginUI
                              key={url.url}
                              {...url}
                              onClick={onClick}
                            />
                          ))}
                        </div>
                      </div>
                    </Col>
                  );
                })
              )}
            </Row>
          </TabPane>
        );
      })}
    </StyledTab>
  );
};

const useCriptioLogin = () => {
  const { data: company } = useCompany();
  const token = useGetToken();
  const [jsx, setJsx] = useState<ReactNode>(null);

  const cancelMitIdSelection = () => {
    localStorage.setItem('AUTH_PROPERTY_ID', '');
    setJsx(null);
  };

  const redirectToMitId = useCallback(() => {
    if (company.authentication_urls.length === 0) {
      // Tenant has an auth method toggled on but no provider URLs were
      // provisioned in the backend, so there's nothing to redirect to.
      // Surface a clear message instead of rendering empty <Tabs/>.
      console.error(
        '[CriptioLogin] authentication_urls is empty — tenant is misconfigured'
      );
      setJsx(
        <div className="rounded-lg bg-secondary p-6 text-center text-sm text-muted-foreground">
          <T _str="Login is not available for this address right now. Please contact your municipality." />
        </div>
      );
      return;
    }
    if (company.authentication_urls.length === 1) {
      // BankID must not receive property_id (BRIDGE-214); the per-provider
      // buttons enforce this by omission, so the single-URL fast path must too.
      //
      // An ID-porten entry cannot reach here in normal operation — Property.store
      // routes an 'idporten' tenant through useIdPortenRedirect instead — and it
      // would be harmless if it did (same query contract, and property_id IS
      // wanted). Nothing special to do.
      const { url, provider } = company.authentication_urls[0];
      window.location.href = createMitIdLink(
        url,
        token,
        provider === 'bankid' ? null : getAuthPropertyId()
      );
    } else {
      setJsx(<CriptioLogin />);
    }
  }, [company.authentication_urls, token]);

  return {
    jsx,
    redirectToMitId,
    cancelMitIdSelection,
  };
};

CriptioLogin.useCriptioLogin = useCriptioLogin;

export { CriptioLogin };
